import React, { memo, useCallback, useEffect, useState } from "react";

import { faCheck, faTag } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isEqual } from "lodash-es";
import { defineMessages, useIntl } from "react-intl";

import { OnlyRequiredFields } from "@mapmycustomers/shared";
import AccessStatus from "@mapmycustomers/shared/types/entity/common/AccessStatus";
import RawFile from "@mapmycustomers/shared/types/File";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import noFiles from "@app/assets/list/preview/no-files.svg";
import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import FileContent from "@app/component/FileContent/FilesContent";
import FileUploaderModal from "@app/component/FileUploaderModal";
import FileListItem from "@app/types/FileListItem";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useModalVisibilityAnalyticEffect from "@app/util/hook/useModalVisibilityAnalyticEffect";

import AddOrCreateButton from "../AddOrCreateButton";
import NoData from "../NoData";
import TabLayout from "../TabLayout";

import styles from "./Files.module.scss";

const messages = defineMessages({
  add: {
    id: "recordPane.fileList.addButton",
    defaultMessage: "Add",
    description: "Title of a button to add a file to a record",
  },
  doneEditing: {
    id: "recordPane.fileList.doneEditing",
    defaultMessage: "Done Editing",
    description: "Done editing tags button",
  },
  editTags: {
    id: "recordPane.fileList.editTags",
    defaultMessage: "Edit Tags",
    description: "Edit tags button",
  },
  noData: {
    id: "recordPane.fileList.noData",
    defaultMessage: "Add a file to populate this list",
    description: "Text of the 'No Data' state in Files section in a Edit pane",
  },
  title: {
    id: "recordPane.fileList.title",
    defaultMessage: "Files {count}",
    description: "Title of a Files section on a record pane",
  },
  uneditable: {
    id: "recordPane.fileList.addButton.uneditable",
    defaultMessage:
      "You do not have permission to use this feature. Please contact your admin to request a change.",
    description: "Tooltip for an Add file Button when user has no edit rights",
  },
  unreadable: {
    id: "recordPane.fileList.unreadable",
    defaultMessage: "Files are hidden",
    description: "Text of the PHI-hidden state in Files section in a Edit pane",
  },
});

interface Props {
  accessStatus?: AccessStatus;
  clearAllUploadedFiles: () => void;
  files: RawFile[];
  fileUploading?: boolean;
  onDelete?: (fileId: RawFile) => void;
  onDownload?: (file: RawFile) => void;
  onFetchThumbnail?: (payload: { callback: (blob: Blob) => void; fileId: RawFile["id"] }) => void;
  onUpdateFiles?: (payload: { files: OnlyRequiredFields<RawFile, "id">[] }) => void;
  onUpload?: (payload: {
    callback?: (filesList: FileListItem[]) => void;
    files: (Blob | File)[];
  }) => void;
  required?: boolean;
  uneditable?: boolean;
  unreadable?: boolean;
}

const EditFilesPanel: React.FC<Props> = ({
  accessStatus,
  clearAllUploadedFiles,
  files,
  fileUploading,
  onDelete,
  onDownload,
  onFetchThumbnail,
  onUpdateFiles,
  onUpload,
  required,
  uneditable,
  unreadable,
}) => {
  const intl = useIntl();
  const [isAddingFile, startAddingFileProcess, stopAddingFileProcess] = useBoolean(false, true);
  const [isEditingTags, startEditingTags, stopEditingTags] = useBoolean(false);
  const [updatedFiles, setUpdatedFiles] = useState<RawFile[]>(files);

  const notesAndFilesAnalytics = useExtendedAnalytics("Notes & Files", useAnalytics());
  useModalVisibilityAnalyticEffect("Add File", isAddingFile, notesAndFilesAnalytics);

  const handleDownload = useCallback(
    (file: RawFile) => {
      notesAndFilesAnalytics?.clicked(["Download File"]);
      onDownload?.(file);
    },
    [notesAndFilesAnalytics, onDownload]
  );

  const handleDelete = useCallback(
    (file: RawFile) => {
      notesAndFilesAnalytics?.clicked(["Delete File"]);
      onDelete?.(file);
    },
    [notesAndFilesAnalytics, onDelete]
  );

  const handleSaveTagEdits = useCallback(() => {
    const fileTagIdsMap = new Map(files.map(({ id, tags }) => [id, tags]));

    const updatedFilesData = updatedFiles
      .filter(({ id, tags }) => !isEqual(fileTagIdsMap.get(id), tags))
      .map(({ id, name, tags }) => ({ id, name, tagIds: (tags ?? []).map(({ id }) => id) }));

    if (updatedFilesData.length) {
      onUpdateFiles?.({
        files: updatedFilesData,
      });
    }

    stopEditingTags();
  }, [files, onUpdateFiles, stopEditingTags, updatedFiles]);

  const handleChange = useCallback((updatedFile: RawFile) => {
    setUpdatedFiles((files) =>
      files.map((file) => (file.id === updatedFile.id ? updatedFile : file))
    );
  }, []);

  const handleFilesChange = useCallback((uploadedFiles: RawFile[]) => {
    const uploadedFilesMap = new Map(uploadedFiles.map((file) => [file.id, file]));
    setUpdatedFiles((files) =>
      files.map((file) => {
        const updatedFile = uploadedFilesMap.get(file.id);
        return updatedFile ?? file;
      })
    );
  }, []);

  useEffect(() => {
    setUpdatedFiles(files);
  }, [files]);

  return (
    <TabLayout
      action={
        <div className={styles.actions}>
          {files.length > 0 &&
            (isEditingTags ? (
              <Button
                leftIcon={<FontAwesomeIcon icon={faCheck} />}
                onClick={handleSaveTagEdits}
                type="filled"
              >
                {intl.formatMessage(messages.doneEditing)}
              </Button>
            ) : (
              <Button
                leftIcon={<FontAwesomeIcon icon={faTag} />}
                onClick={startEditingTags}
                type="link"
              >
                {intl.formatMessage(messages.editTags)}
              </Button>
            ))}
          <AddOrCreateButton
            disabled={unreadable || uneditable}
            label={intl.formatMessage(messages.add)}
            onClick={startAddingFileProcess}
            tooltip={unreadable || uneditable ? intl.formatMessage(messages.uneditable) : undefined}
          />
        </div>
      }
      title={intl.formatMessage(messages.title, {
        count: unreadable ? "" : <span className={styles.total}>({files.length})</span>,
      })}
    >
      {files.length > 0 && !unreadable ? (
        <div>
          <FileContent
            accessStatus={accessStatus}
            canDelete={!uneditable}
            editingTags={isEditingTags}
            files={updatedFiles}
            onChange={handleChange}
            onDelete={handleDelete}
            onDownload={handleDownload}
            onFetchThumbnail={onFetchThumbnail}
            required={required}
            supportsTags
          />
        </div>
      ) : (
        <NoData
          imageSrc={noFiles}
          text={
            unreadable
              ? intl.formatMessage(messages.unreadable)
              : intl.formatMessage(messages.noData)
          }
        />
      )}

      {isAddingFile && (
        <ExtendedAnalytics keyPath="Notes & Files">
          <FileUploaderModal
            clearAllUploadedFiles={clearAllUploadedFiles}
            fileUploading={fileUploading}
            onChange={handleFilesChange}
            onDelete={onDelete}
            onHide={stopAddingFileProcess}
            onUpload={onUpload}
            supportsTags
          />
        </ExtendedAnalytics>
      )}
    </TabLayout>
  );
};

export default memo(EditFilesPanel);
